/**
 * Plugin Menu Registry
 *
 * Frontend registry for plugin-provided menu items.
 * Works in conjunction with backend PluginMenuRegistryService.
 * Part of Phase 0: Core Preparation for Modularization.
 */

import { create } from 'zustand';

/**
 * Menu locations
 */
export type MenuLocation =
  | 'main-menu'
  | 'context-menu'
  | 'entity-actions'
  | 'toolbar-menu'
  | 'user-menu'
  | 'help-menu';

/**
 * Menu item definition
 */
export interface MenuItem {
  /** Unique item ID */
  id: string;
  /** Plugin that owns this item */
  pluginId: string;
  /** Menu location */
  location: MenuLocation;
  /** Item label */
  label: string;
  /** Item icon */
  icon?: string;
  /** Keyboard shortcut */
  shortcut?: string;
  /** Command to execute */
  command?: string;
  /** URL to navigate to */
  url?: string;
  /** Order within location */
  order: number;
  /** Parent item ID for submenus */
  parentId?: string;
  /** Whether item is a separator */
  isSeparator?: boolean;
  /** Whether item is disabled */
  disabled?: boolean;
  /** Condition for visibility */
  condition?: MenuCondition;
  /** Sub-menu items */
  children?: MenuItem[];
}

/**
 * Menu condition for visibility
 */
export interface MenuCondition {
  /** Required entity types */
  entityTypes?: string[];
  /** Required permissions */
  permissions?: string[];
  /** Custom when expression */
  when?: string;
}

/**
 * Context for evaluating menu conditions
 */
export interface MenuContext {
  entityType?: string;
  entityId?: string;
  selection?: any[];
  permissions?: string[];
  [key: string]: any;
}

/**
 * Menu registry state
 */
interface MenuRegistryState {
  /** Map of item ID to definition */
  items: Map<string, MenuItem>;
  /** Map of location to item IDs */
  itemsByLocation: Map<MenuLocation, Set<string>>;
  
  // Actions
  registerItem: (item: MenuItem) => void;
  unregisterItem: (itemId: string) => void;
  unregisterPluginItems: (pluginId: string) => void;
  getItem: (itemId: string) => MenuItem | undefined;
  getItemsForLocation: (location: MenuLocation, context?: MenuContext) => MenuItem[];
  getContextMenuItems: (context: MenuContext) => MenuItem[];
  syncFromBackend: (items: MenuItem[]) => void;
}

/**
 * Evaluate a menu condition against context
 */
function evaluateCondition(condition: MenuCondition | undefined, context: MenuContext): boolean {
  if (!condition) return true;
  
  // Check entity types
  if (condition.entityTypes?.length) {
    if (!context.entityType || !condition.entityTypes.includes(context.entityType)) {
      return false;
    }
  }
  
  // Check permissions
  if (condition.permissions?.length) {
    if (!context.permissions) return false;
    for (const perm of condition.permissions) {
      if (!context.permissions.includes(perm)) {
        return false;
      }
    }
  }
  
  // Custom when expression would be evaluated here
  // For now, just return true if we pass other checks
  
  return true;
}

/**
 * Build menu tree from flat items
 */
function buildMenuTree(items: MenuItem[]): MenuItem[] {
  const itemMap = new Map<string, MenuItem>();
  const roots: MenuItem[] = [];
  
  // First pass: create map and initialize children arrays
  for (const item of items) {
    itemMap.set(item.id, { ...item, children: [] });
  }
  
  // Second pass: build tree
  for (const item of items) {
    const menuItem = itemMap.get(item.id)!;
    if (item.parentId) {
      const parent = itemMap.get(item.parentId);
      if (parent) {
        parent.children = parent.children ?? [];
        parent.children.push(menuItem);
      } else {
        roots.push(menuItem);
      }
    } else {
      roots.push(menuItem);
    }
  }
  
  // Sort all levels by order
  const sortItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .sort((a, b) => a.order - b.order)
      .map(item => ({
        ...item,
        children: item.children ? sortItems(item.children) : undefined,
      }));
  };
  
  return sortItems(roots);
}

/**
 * Menu registry store
 */
export const useMenuRegistry = create<MenuRegistryState>((set, get) => ({
  items: new Map(),
  itemsByLocation: new Map(),

  registerItem: (item: MenuItem) => {
    set((state) => {
      const newItems = new Map(state.items);
      const newByLocation = new Map(state.itemsByLocation);
      
      // Remove from old location if updating
      const existing = newItems.get(item.id);
      if (existing) {
        const oldLocationItems = newByLocation.get(existing.location);
        if (oldLocationItems) {
          oldLocationItems.delete(item.id);
        }
      }
      
      newItems.set(item.id, item);
      
      if (!newByLocation.has(item.location)) {
        newByLocation.set(item.location, new Set());
      }
      newByLocation.get(item.location)!.add(item.id);
      
      return { items: newItems, itemsByLocation: newByLocation };
    });
  },

  unregisterItem: (itemId: string) => {
    set((state) => {
      const item = state.items.get(itemId);
      if (!item) return state;
      
      const newItems = new Map(state.items);
      const newByLocation = new Map(state.itemsByLocation);
      
      newItems.delete(itemId);
      newByLocation.get(item.location)?.delete(itemId);
      
      return { items: newItems, itemsByLocation: newByLocation };
    });
  },

  unregisterPluginItems: (pluginId: string) => {
    set((state) => {
      const newItems = new Map(state.items);
      const newByLocation = new Map(state.itemsByLocation);
      
      for (const [id, item] of newItems) {
        if (item.pluginId === pluginId) {
          newItems.delete(id);
          newByLocation.get(item.location)?.delete(id);
        }
      }
      
      return { items: newItems, itemsByLocation: newByLocation };
    });
  },

  getItem: (itemId: string) => {
    return get().items.get(itemId);
  },

  getItemsForLocation: (location: MenuLocation, context?: MenuContext) => {
    const state = get();
    const itemIds = state.itemsByLocation.get(location);
    if (!itemIds) return [];
    
    const items = Array.from(itemIds)
      .map(id => state.items.get(id))
      .filter((item): item is MenuItem => 
        item !== undefined && 
        !item.disabled &&
        evaluateCondition(item.condition, context ?? {})
      );
    
    return buildMenuTree(items);
  },

  getContextMenuItems: (context: MenuContext) => {
    return get().getItemsForLocation('context-menu', context);
  },

  syncFromBackend: (items: MenuItem[]) => {
    set(() => {
      const newItems = new Map<string, MenuItem>();
      const newByLocation = new Map<MenuLocation, Set<string>>();
      
      for (const item of items) {
        newItems.set(item.id, item);
        
        if (!newByLocation.has(item.location)) {
          newByLocation.set(item.location, new Set());
        }
        newByLocation.get(item.location)!.add(item.id);
      }
      
      return { items: newItems, itemsByLocation: newByLocation };
    });
  },
}));

/**
 * Hook to get menu items for a specific location
 */
export function useMenuItems(location: MenuLocation, context?: MenuContext): MenuItem[] {
  return useMenuRegistry((state) => {
    const itemIds = state.itemsByLocation.get(location);
    if (!itemIds) return [];
    
    const items = Array.from(itemIds)
      .map(id => state.items.get(id))
      .filter((item): item is MenuItem => 
        item !== undefined && 
        !item.disabled &&
        evaluateCondition(item.condition, context ?? {})
      );
    
    return buildMenuTree(items);
  });
}
